import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  IconButton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Avatar,
  Tabs,
  Tab,
  Container,
  Chip,
  Stack,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  EmojiEvents as AwardIcon,
  Timeline as TimelineIcon,
  Link as LinkIcon,
  Translate as TranslateIcon,
  Star as StarIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  Task as TaskIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import { aboutPageService, globalService } from '../../services/firebase';
import { AboutPageData, TeamMember, Milestone, Certificate } from '../../types';
import ImageUploadField from '../../components/Common/ImageUploadField';
import { produce, Draft } from 'immer';
import { useTranslation } from 'react-i18next';
import { getTranslatedData, setTranslatedData } from '../../utils/getTranslatedData';
import 'react-quill/dist/quill.snow.css';

const ManageAboutPage: React.FC = () => {
  const [aboutData, setAboutData] = useState<AboutPageData | null>(null);
  const [initialData, setInitialData] = useState<AboutPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const [dialogLang, setDialogLang] = useState('tr');
  const { t } = useTranslation();
  const [translatingSection, setTranslatingSection] = useState<string | null>(null);

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    type: string;
    data: any;
    isNew: boolean;
  }>({
    open: false,
    type: '',
    data: null,
    isNew: false,
  });

  const getStrictTranslatedData = (data: any, lang: string): string => {
    if (typeof data === 'object' && data !== null) {
      return data[lang] || '';
    }
    if (typeof data === 'string' && lang === 'tr') {
      return data;
    }
    return '';
  };

  const fetchAboutData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let data = await aboutPageService.getAboutPageData();
      
      if (!data) {
        await aboutPageService.initializeAboutPageData();
        data = await aboutPageService.getAboutPageData();
      }
      
      const finalData = produce((data || {}) as AboutPageData, (draft: Draft<AboutPageData>) => {
        const emptyTranslatable = { tr: '', en: '', ar: '' };
        const defaultSection = { isActive: true };

        if (!draft.sections) draft.sections = {} as any;
        draft.sections.statistics = { ...defaultSection, ...draft.sections.statistics };
        draft.sections.missionVision = { ...defaultSection, ...draft.sections.missionVision };
        draft.sections.values = { ...defaultSection, ...draft.sections.values };
        draft.sections.team = { ...defaultSection, ...draft.sections.team };
        draft.sections.history = { ...defaultSection, ...draft.sections.history };
        draft.sections.certificates = { ...defaultSection, ...draft.sections.certificates };
        
        if(!draft.heroSection) draft.heroSection = {} as any;
        draft.heroSection.title = draft.heroSection.title || emptyTranslatable;
        draft.heroSection.subtitle = draft.heroSection.subtitle || emptyTranslatable;
        draft.heroSection.description = draft.heroSection.description || emptyTranslatable;
        
        if(!draft.statistics) draft.statistics = {} as any;
        draft.statistics.yearsExperience = draft.statistics.yearsExperience || 0;
        draft.statistics.happyCustomers = draft.statistics.happyCustomers || 0;
        draft.statistics.toursCompleted = draft.statistics.toursCompleted || 0;
        draft.statistics.destinations = draft.statistics.destinations || 0;

        if (!draft.missionVision) draft.missionVision = {} as any;
        draft.missionVision.mission = draft.missionVision.mission || emptyTranslatable;
        draft.missionVision.vision = draft.missionVision.vision || emptyTranslatable;
        draft.missionVision.values = draft.missionVision.values || [];
        
        if (!draft.companyHistory) draft.companyHistory = {} as any;
        draft.companyHistory.milestones = draft.companyHistory.milestones || [];
        
        draft.teamMembers = draft.teamMembers || [];
        draft.certificates = draft.certificates || [];
      });

      setAboutData(finalData);
      setInitialData(JSON.parse(JSON.stringify(finalData)));
    } catch (err: any) {
      console.error('Hakkımızda verileri yüklenirken hata:', err);
      setError(`Sayfa verileri yüklenirken bir hata oluştu: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAboutData();
  }, [fetchAboutData]);

  const handleTranslate = async (path: string, fields: string[]) => {
    setTranslatingSection(path);
    try {
        const keys = path.split('.');
        let dataToTranslate: any = { ...aboutData };
        for (const key of keys) {
            dataToTranslate = dataToTranslate?.[key];
        }

        if (!dataToTranslate) {
            setError(`Çevrilecek veri bulunamadı: ${path}`);
            return;
        }

        let translations: { [key: string]: { en: string; ar: string } } = {};

        for (const field of fields) {
            const sourceText = getTranslatedData(dataToTranslate[field], 'tr');
            if (sourceText) {
                translations[field] = await globalService.translateTexts({
                    text: sourceText,
                    sourceLang: 'tr',
                    targetLangs: ['en', 'ar'],
                }) as { en: string; ar: string };
            }
        }
        
        setAboutData(produce(aboutData, (draft: any) => {
            let draftSlice = draft;
            for (const key of keys) {
                draftSlice = draftSlice[key];
            }
            for (const field of fields) {
                if (translations[field]) {
                    draftSlice[field] = setTranslatedData(draftSlice[field], field, 'en', translations[field].en);
                    draftSlice[field] = setTranslatedData(draftSlice[field], field, 'ar', translations[field].ar);
                }
            }
        }));

        setSuccess('Bölüm başarıyla çevrildi!');
    } catch (err) {
        console.error("Çeviri hatası:", err);
        setError('Çeviri sırasında bir hata oluştu.');
    } finally {
        setTranslatingSection(null);
    }
};


  const handleDialogTranslate = async () => {
    if (!dialogState.data) return;
  
    const fieldsMap: { [key: string]: string[] } = {
      team: ['name', 'position', 'bio'],
      milestone: ['title', 'description'],
      certificate: ['name', 'issuer', 'description'],
    };
  
    const fieldsToTranslate = fieldsMap[dialogState.type];
    if (!fieldsToTranslate) return;
  
    setTranslatingSection('dialog');
    try {
      let updatedData = { ...dialogState.data };
  
      for (const field of fieldsToTranslate) {
        const sourceText = getTranslatedData(updatedData[field], 'tr');
        if (sourceText) {
          const translations = await globalService.translateTexts({
            text: sourceText,
            sourceLang: 'tr',
            targetLangs: ['en', 'ar'],
          });
          updatedData = setTranslatedData(updatedData, field, 'en', translations.en);
          updatedData = setTranslatedData(updatedData, field, 'ar', translations.ar);
        }
      }
      setDialogState(prev => ({ ...prev, data: updatedData }));
      setSuccess('İçerik başarıyla çevrildi!');
    } catch (err: any) {
      setError(`Çeviri sırasında bir hata oluştu: ${err.message}`);
    } finally {
      setTranslatingSection(null);
    }
  };

  const getNestedData = (path: string) => {
    if (!aboutData) return '';
    return path.split('.').reduce((obj: any, key: string) => obj && obj[key], aboutData);
  }
  
  const getTranslatedNestedData = (path: string, lang: string) => {
    const data = getNestedData(path);
    return getStrictTranslatedData(data, lang);
  }

  const handleTranslatableFieldChange = (path: string, value: string) => {
    setAboutData(produce(aboutData, (draft: Draft<AboutPageData | null>) => {
        if (!draft) return;
        const keys = path.split('.');
        let current: any = draft;
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]] = current[keys[i]] || {};
        }
        const fieldName = keys[keys.length - 1];
        
        const existingValue = current[fieldName];
        let fieldAsObject = {};

        if (typeof existingValue === 'object' && existingValue !== null) {
            fieldAsObject = existingValue;
        } else if (typeof existingValue === 'string') {
            fieldAsObject = { tr: existingValue };
        }
        
        current[fieldName] = {
            ...fieldAsObject,
            [selectedLang]: value,
        };
    }));
  };

  const handleSimpleFieldChange = (path: string, value: any) => {
    setAboutData(produce(aboutData, (draft: any) => {
        const keys = path.split('.');
        let current = draft;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (current[key] === undefined || typeof current[key] !== 'object' || current[key] === null) {
            current[key] = {};
          }
          current = current[key];
        }
        current[keys[keys.length - 1]] = value;
    }));
  };

  const handleSave = async () => {
    if (!aboutData) return;
    try {
      setSaving(true);
      setError(null);
      
      const dataToSave = JSON.parse(JSON.stringify(aboutData));
      delete (dataToSave as any).id;
      
      await aboutPageService.updateAboutPageData(dataToSave);
      
      setInitialData(JSON.parse(JSON.stringify(aboutData)));
      setSuccess('Hakkımızda sayfası başarıyla güncellendi!');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      console.error('Kaydetme hatası:', err);
      setError(`Kaydetme sırasında bir hata oluştu: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const openDialog = (type: string, data: any = null) => {
    let defaultData: any = {};
    const isNew = data === null;
    let collection: any[] = [];
    
    if (type === 'team') collection = aboutData?.teamMembers || [];
    else if (type === 'milestone') collection = aboutData?.companyHistory.milestones || [];
    else if (type === 'certificate') collection = aboutData?.certificates || [];

    switch (type) {
        case 'team': defaultData = { id: Date.now().toString(), name: {tr:'', en:'', ar:''}, position: {tr:'', en:'', ar:''}, bio: {tr:'', en:'', ar:''}, image: '', order: collection.length + 1, isActive: true, socialMedia: { linkedin: '', twitter: '', instagram: '' } }; break;
        case 'milestone': defaultData = { id: Date.now().toString(), year: new Date().getFullYear(), title: {tr:'', en:'', ar:''}, description: {tr:'', en:'', ar:''}, order: collection.length + 1, isActive: true }; break;
        case 'certificate': defaultData = { id: Date.now().toString(), name: {tr:'', en:'', ar:''}, issuer: {tr:'', en:'', ar:''}, year: new Date().getFullYear(), image: '', description: {tr:'', en:'', ar:''}, order: collection.length + 1, isActive: true }; break;
    }
    
    setDialogLang('tr');
    setDialogState({ open: true, type, data: isNew ? defaultData : data, isNew });
  };
  
  const closeDialog = () => {
    setDialogState({ open: false, type: '', data: null, isNew: false });
  };
  
  const handleDialogDataChange = (field: string, value: any, isTranslatable: boolean = false) => {
    setDialogState(prev => produce(prev, draft => {
        if (!draft.data) return;
        if (isTranslatable) {
            draft.data[field] = { ...(draft.data[field] || {}), [dialogLang]: value };
        } else {
            draft.data[field] = value;
        }
    }));
  };

  const handleSocialMediaChange = (platform: 'linkedin' | 'twitter' | 'instagram', value: string) => {
    setDialogState(prev => produce(prev, draft => {
        if (!draft.data.socialMedia) draft.data.socialMedia = {};
        draft.data.socialMedia[platform] = value;
    }));
  };

  const handleSaveItem = () => {
    if (!dialogState.type || !dialogState.data) return;

    const collectionMap: any = {
      team: 'teamMembers',
      milestone: 'companyHistory.milestones',
      certificate: 'certificates'
    };
    const collectionName = collectionMap[dialogState.type];
    
    setAboutData(produce(aboutData, (draft: Draft<any>) => {
      const path = collectionName.split('.');
      let collectionRef = draft;
      for (let i = 0; i < path.length -1; i++) {
        collectionRef = collectionRef[path[i]];
      }
      let collection = collectionRef[path[path.length - 1]];

      if (dialogState.isNew) {
        collection.push(dialogState.data);
      } else {
        const itemIndex = collection.findIndex((item: any) => item.id === dialogState.data.id);
        if (itemIndex > -1) {
          collection[itemIndex] = dialogState.data;
        }
      }
    }));

    closeDialog();
  };
  
  const handleDeleteItem = (type: string, id: string) => {
    const collectionMap: any = {
      team: 'teamMembers',
      milestone: 'companyHistory.milestones',
      certificate: 'certificates'
    };
    const collectionName = collectionMap[type];

    setAboutData(produce(aboutData, (draft: any) => {
        const path = collectionName.split('.');
        let collectionRef = draft;
        for (let i = 0; i < path.length - 1; i++) {
            collectionRef = collectionRef[path[i]];
        }
        const collection = collectionRef[path[path.length - 1]];
        const updatedCollection = collection.filter((item: any) => item.id !== id);
        collectionRef[path[path.length - 1]] = updatedCollection;
    }));
  };

  const handleValueChange = (index: number, value: string) => {
    handleSimpleFieldChange(`missionVision.values.${index}`, value);
  };
  const addValue = () => {
    const newValues = [...(aboutData?.missionVision?.values || []), 'Yeni Değer'];
    handleSimpleFieldChange('missionVision.values', newValues);
  };
  const removeValue = (index: number) => {
    const newValues = [...(aboutData?.missionVision?.values || [])];
    newValues.splice(index, 1);
    handleSimpleFieldChange('missionVision.values', newValues);
  };

  const renderDialogContent = () => {
    if (!dialogState.data) return null;

    const { type, data } = dialogState;
    const isTranslating = translatingSection === 'dialog';

    return (
      <DialogContent>
        <Tabs value={dialogLang} onChange={(_, newValue) => setDialogLang(newValue)} sx={{ mb: 2 }}>
          <Tab label="Türkçe" value="tr" />
          <Tab label="English" value="en" />
          <Tab label="العربية" value="ar" />
        </Tabs>
        {type === 'team' && (
          <>
            <TextField label={`İsim (${dialogLang})`} fullWidth margin="normal" value={getStrictTranslatedData(data.name, dialogLang)} onChange={(e) => handleDialogDataChange('name', e.target.value, true)}/>
            <TextField label={`Pozisyon (${dialogLang})`} fullWidth margin="normal" value={getStrictTranslatedData(data.position, dialogLang)} onChange={(e) => handleDialogDataChange('position', e.target.value, true)}/>
            <TextField label={`Biyografi (${dialogLang})`} multiline rows={4} fullWidth margin="normal" value={getStrictTranslatedData(data.bio, dialogLang)} onChange={(e) => handleDialogDataChange('bio', e.target.value, true)}/>
            <ImageUploadField label="Profil Resmi" storagePath="team" value={data.image} onChange={(url) => handleDialogDataChange('image', url)} />
            <TextField label="LinkedIn URL" fullWidth margin="normal" value={data.socialMedia?.linkedin || ''} onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)} InputProps={{startAdornment: <LinkIcon color="action" sx={{mr:1}}/>}}/>
            <TextField label="Twitter URL" fullWidth margin="normal" value={data.socialMedia?.twitter || ''} onChange={(e) => handleSocialMediaChange('twitter', e.target.value)} InputProps={{startAdornment: <LinkIcon color="action" sx={{mr:1}}/>}}/>
            <TextField label="Instagram URL" fullWidth margin="normal" value={data.socialMedia?.instagram || ''} onChange={(e) => handleSocialMediaChange('instagram', e.target.value)} InputProps={{startAdornment: <LinkIcon color="action" sx={{mr:1}}/>}}/>
          </>
        )}
        {type === 'milestone' && (
          <>
            <TextField label="Yıl" type="number" fullWidth margin="normal" value={data.year} onChange={(e) => handleDialogDataChange('year', parseInt(e.target.value, 10) || new Date().getFullYear())}/>
            <TextField label={`Başlık (${dialogLang})`} fullWidth margin="normal" value={getStrictTranslatedData(data.title, dialogLang)} onChange={(e) => handleDialogDataChange('title', e.target.value, true)}/>
            <TextField label={`Açıklama (${dialogLang})`} multiline rows={3} fullWidth margin="normal" value={getStrictTranslatedData(data.description, dialogLang)} onChange={(e) => handleDialogDataChange('description', e.target.value, true)}/>
          </>
        )}
        {type === 'certificate' && (
          <>
            <TextField label={`Sertifika Adı (${dialogLang})`} fullWidth margin="normal" value={getStrictTranslatedData(data.name, dialogLang)} onChange={(e) => handleDialogDataChange('name', e.target.value, true)}/>
            <TextField label={`Veren Kurum (${dialogLang})`} fullWidth margin="normal" value={getStrictTranslatedData(data.issuer, dialogLang)} onChange={(e) => handleDialogDataChange('issuer', e.target.value, true)}/>
            <TextField label={`Açıklama (${dialogLang})`} multiline rows={3} fullWidth margin="normal" value={getStrictTranslatedData(data.description, dialogLang)} onChange={(e) => handleDialogDataChange('description', e.target.value, true)}/>
            <TextField label="Yıl" type="number" fullWidth margin="normal" value={data.year} onChange={(e) => handleDialogDataChange('year', e.target.value)}/>
            <ImageUploadField label="Sertifika Resmi" storagePath="certificates" value={data.image} onChange={(url) => handleDialogDataChange('image', url)}/>
          </>
        )}
        <FormControlLabel control={<Switch checked={data.isActive ?? true} onChange={(e) => handleDialogDataChange('isActive', e.target.checked)}/>} label="Aktif"/>
        <DialogActions>
            <Button startIcon={<TranslateIcon />} onClick={handleDialogTranslate} disabled={isTranslating}>{isTranslating ? 'Çevriliyor...' : 'Tüm Alanları Çevir'}</Button>
            <Button onClick={closeDialog}>İptal</Button>
            <Button onClick={handleSaveItem} variant="contained">Kaydet</Button>
        </DialogActions>
      </DialogContent>
    );
  };

  if (loading) return <Box display="flex" justifyContent="center" my={5}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!aboutData) return <Alert severity="info">Hakkımızda verisi bulunamadı.</Alert>;
  
  const isTranslating = (path: string) => translatingSection === path;

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: { xs: 2, md: 4 }, my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" component="h1">Hakkımızda Sayfası Yönetimi</Typography>
          <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || loading || JSON.stringify(aboutData) === JSON.stringify(initialData)}>{saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</Button>
        </Box>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <Tabs value={selectedLang} onChange={(_, newValue) => setSelectedLang(newValue)} sx={{ mb: 2 }}>
            <Tab label="Türkçe" value="tr" />
            <Tab label="English" value="en" />
            <Tab label="العربية" value="ar" />
        </Tabs>
        
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><FlagIcon sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6">Hero Bölümü</Typography></AccordionSummary>
          <AccordionDetails>
            <TextField label={`Ana Başlık (${selectedLang})`} fullWidth value={getTranslatedNestedData('heroSection.title', selectedLang)} onChange={(e) => handleTranslatableFieldChange('heroSection.title', e.target.value)} margin="normal"/>
            <TextField label={`Alt Başlık (${selectedLang})`} fullWidth value={getTranslatedNestedData('heroSection.subtitle', selectedLang)} onChange={(e) => handleTranslatableFieldChange('heroSection.subtitle', e.target.value)} margin="normal"/>
            <TextField label={`Açıklama (${selectedLang})`} multiline rows={4} fullWidth value={getTranslatedNestedData('heroSection.description', selectedLang)} onChange={(e) => handleTranslatableFieldChange('heroSection.description', e.target.value)} margin="normal"/>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button startIcon={<TranslateIcon />} onClick={() => handleTranslate('heroSection', ['title', 'subtitle', 'description'])} disabled={isTranslating('heroSection')}>{isTranslating('heroSection') ? 'Çevriliyor...' : 'Bu Bölümü Çevir'}</Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><StarIcon sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6">İstatistikler</Typography><FormControlLabel sx={{ ml: 'auto' }} control={<Switch checked={aboutData.sections?.statistics?.isActive ?? true} onChange={(e) => handleSimpleFieldChange('sections.statistics.isActive', e.target.checked)} />} label="Aktif"/></AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <TextField label="Yıllık Tecrübe" type="number" fullWidth value={aboutData.statistics?.yearsExperience || 0} onChange={(e) => handleSimpleFieldChange('statistics.yearsExperience', parseInt(e.target.value, 10))} />
                <TextField label="Mutlu Müşteriler" type="number" fullWidth value={aboutData.statistics?.happyCustomers || 0} onChange={(e) => handleSimpleFieldChange('statistics.happyCustomers', parseInt(e.target.value, 10))} />
                <TextField label="Tamamlanan Turlar" type="number" fullWidth value={aboutData.statistics?.toursCompleted || 0} onChange={(e) => handleSimpleFieldChange('statistics.toursCompleted', parseInt(e.target.value, 10))} />
                <TextField label="Destinasyonlar" type="number" fullWidth value={aboutData.statistics?.destinations || 0} onChange={(e) => handleSimpleFieldChange('statistics.destinations', parseInt(e.target.value, 10))} />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><BusinessIcon sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6">Misyon, Vizyon & Değerler</Typography><FormControlLabel sx={{ ml: 'auto' }} control={<Switch checked={aboutData.sections?.missionVision?.isActive ?? true} onChange={(e) => handleSimpleFieldChange('sections.missionVision.isActive', e.target.checked)} />} label="Aktif"/></AccordionSummary>
          <AccordionDetails>
            <TextField label={`Misyon (${selectedLang})`} multiline rows={4} fullWidth value={getTranslatedNestedData('missionVision.mission', selectedLang)} onChange={(e) => handleTranslatableFieldChange('missionVision.mission', e.target.value)} margin="normal"/>
            <TextField label={`Vizyon (${selectedLang})`} multiline rows={4} fullWidth value={getTranslatedNestedData('missionVision.vision', selectedLang)} onChange={(e) => handleTranslatableFieldChange('missionVision.vision', e.target.value)} margin="normal"/>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button startIcon={<TranslateIcon />} onClick={() => handleTranslate('missionVision', ['mission', 'vision'])} disabled={isTranslating('missionVision')}>{isTranslating('missionVision') ? 'Çevriliyor...' : 'Bu Bölümü Çevir'}</Button>
            </Box>
            <Typography variant="h6" sx={{mt: 4, mb: 2}}>Değerler</Typography>
            <Stack spacing={2}>
              {aboutData.missionVision?.values?.map((value: string, index: number) => (
                <Stack direction="row" spacing={1} key={index}>
                  <TextField fullWidth value={value} onChange={(e) => handleValueChange(index, e.target.value)} label={`Değer ${index + 1}`}/>
                  <IconButton onClick={() => removeValue(index)} color="error"><DeleteIcon /></IconButton>
                </Stack>
              ))}
            </Stack>
            <Button startIcon={<AddIcon />} onClick={addValue} sx={{ mt: 2 }}>Değer Ekle</Button>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><TimelineIcon sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6">Şirket Tarihçesi</Typography><FormControlLabel sx={{ ml: 'auto' }} control={<Switch checked={aboutData.sections?.history?.isActive ?? true} onChange={(e) => handleSimpleFieldChange('sections.history.isActive', e.target.checked)} />} label="Aktif"/></AccordionSummary>
            <AccordionDetails>
                <Button startIcon={<AddIcon />} onClick={() => openDialog('milestone')} sx={{ my: 1 }}>Kilometre Taşı Ekle</Button>
                {aboutData.companyHistory?.milestones?.map((m: Milestone) => (
                    <Card key={m.id} sx={{ my: 1, p: 1 }}>
                        <CardContent sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Typography>{m.year} - {getStrictTranslatedData(m.title, 'tr')}</Typography>
                            <Box>
                                <FormControlLabel control={<Switch checked={m.isActive} onChange={(e) => handleSimpleFieldChange(`companyHistory.milestones.${aboutData.companyHistory.milestones.findIndex(i => i.id === m.id)}.isActive`, e.target.checked)}/>} label="Aktif"/>
                                <IconButton onClick={() => openDialog('milestone', m)}><EditIcon /></IconButton>
                                <IconButton onClick={() => handleDeleteItem('milestone', m.id)}><DeleteIcon /></IconButton>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </AccordionDetails>
        </Accordion>

        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><GroupIcon sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6">Ekip Üyeleri</Typography><FormControlLabel sx={{ ml: 'auto' }} control={<Switch checked={aboutData.sections?.team?.isActive ?? true} onChange={(e) => handleSimpleFieldChange('sections.team.isActive', e.target.checked)} />} label="Aktif"/></AccordionSummary>
            <AccordionDetails>
                <Button startIcon={<AddIcon />} onClick={() => openDialog('team')} sx={{ mb: 2 }}>Ekip Üyesi Ekle</Button>
                {aboutData.teamMembers?.map((member: TeamMember) => (
                     <Card key={member.id} sx={{ display: 'flex', mb: 2, alignItems: 'center', p:1 }}>
                        <Avatar src={member.image} sx={{ width: 60, height: 60, m: 2 }} />
                        <CardContent sx={{ flex: 1 }}>
                            <Typography variant="h6">{getStrictTranslatedData(member.name, 'tr')}</Typography>
                            <Typography color="text.secondary">{getStrictTranslatedData(member.position, 'tr')}</Typography>
                        </CardContent>
                        <Box sx={{p:2}}>
                           <FormControlLabel control={<Switch checked={member.isActive} onChange={(e) => handleSimpleFieldChange(`teamMembers.${aboutData.teamMembers.findIndex(i => i.id === member.id)}.isActive`, e.target.checked)}/>} label="Aktif"/>
                           <IconButton onClick={() => openDialog('team', member)}><EditIcon /></IconButton>
                           <IconButton onClick={() => handleDeleteItem('team', member.id)}><DeleteIcon /></IconButton>
                        </Box>
                     </Card>
                ))}
            </AccordionDetails>
        </Accordion>

        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}><AwardIcon sx={{ mr: 1, color: 'primary.main' }} /><Typography variant="h6">Sertifikalar</Typography><FormControlLabel sx={{ ml: 'auto' }} control={<Switch checked={aboutData.sections?.certificates?.isActive ?? true} onChange={(e) => handleSimpleFieldChange('sections.certificates.isActive', e.target.checked)} />} label="Aktif"/></AccordionSummary>
            <AccordionDetails>
                <Button startIcon={<AddIcon />} onClick={() => openDialog('certificate')} sx={{ mb: 2 }}>Sertifika Ekle</Button>
                {aboutData.certificates?.map((cert: Certificate) => (
                    <Card key={cert.id} sx={{ display: 'flex', mb: 2, alignItems: 'center', p:1 }}>
                        <Avatar src={cert.image} variant="rounded" sx={{ width: 60, height: 60, m: 2 }} />
                        <CardContent sx={{ flex: 1 }}>
                            <Typography variant="h6">{getStrictTranslatedData(cert.name, 'tr')}</Typography>
                            <Typography color="text.secondary">{getStrictTranslatedData(cert.issuer, 'tr')} - {cert.year}</Typography>
                        </CardContent>
                        <Box sx={{p:2}}>
                            <FormControlLabel control={<Switch checked={cert.isActive} onChange={(e) => handleSimpleFieldChange(`certificates.${aboutData.certificates.findIndex(i => i.id === cert.id)}.isActive`, e.target.checked)}/>} label="Aktif"/>
                            <IconButton onClick={() => openDialog('certificate', cert)}><EditIcon /></IconButton>
                            <IconButton onClick={() => handleDeleteItem('certificate', cert.id)}><DeleteIcon /></IconButton>
                        </Box>
                    </Card>
                ))}
            </AccordionDetails>
        </Accordion>
      </Paper>
      
      <Dialog open={dialogState.open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{dialogState.isNew ? 'Yeni Ekle' : 'Düzenle'}</DialogTitle>
        {renderDialogContent()}
      </Dialog>
    </Container>
  );
};

export default ManageAboutPage; 